import { themeV2 as theme } from '@features/executive-report/domain/theme-v2';
import { arrowFromValue } from '@features/executive-report/slides/v2/components/arrow-from-value';
import { cardBackground } from '@features/executive-report/slides/v2/components/card-background';
import { Slide, TextProps } from '@features/powerpoint';
import { formatters, ValueInput } from '@utils/formattersV2';

const CARD_Y_OFFSET = 1.34;
const CARD_METRIC_PART_X_OFFSET = 0.9;

type CardProps = {
  title: TextProps;
  kpi: TextProps;
  description: TextProps;
  metricName: TextProps;
  metricParts: {
    name: TextProps;
    value: TextProps;
  }[];
  arrowDirection: 'up' | 'down';
  index: number;
};

const buildCard = (
  props: CardProps
): Required<Pick<Slide, 'shapes' | 'texts'>> => {
  const driverArrow = arrowFromValue(props.arrowDirection === 'up' ? 1 : -1, {
    h: 0.26,
    w: 0,
    x: 9.03,
    y: 3.48 + props.index * CARD_Y_OFFSET,
  });

  return {
    texts: [
      {
        ...props.title,
        options: {
          ...props.title.options,
          h: 0.3,
          w: 1.88,
          x: 8.94,
          y: 3.12 + props.index * CARD_Y_OFFSET,
        },
      },
      {
        ...props.kpi,
        options: {
          ...props.kpi.options,
          h: 0.57,
          w: 1.81,
          x: 9.19,
          y: 3.32 + props.index * CARD_Y_OFFSET,
        },
      },
      {
        ...props.description,
        options: {
          ...props.description.options,
          h: 0.55,
          w: 2.06,
          x: 8.88,
          y: 3.8 + props.index * CARD_Y_OFFSET,
        },
      },
      {
        ...props.metricName,
        options: {
          ...props.metricName.options,
          align: 'center',
          h: 0.22,
          w: 1.85,
          x: 10.95,
          y: 3.14 + props.index * CARD_Y_OFFSET,
        },
      },
      ...props.metricParts.flatMap((part, partIndex): TextProps[] => [
        {
          ...part.name,
          options: {
            ...part.name.options,
            align: 'center',
            h: 0.22,
            w: 0.81,
            x: 10.98 + partIndex * CARD_METRIC_PART_X_OFFSET,
            y: 3.55 + props.index * CARD_Y_OFFSET,
          },
        },
        {
          ...part.value,
          options: {
            ...part.value.options,
            align: 'center',
            h: 0.4,
            w: 1.28,
            x: 10.78 + partIndex * CARD_METRIC_PART_X_OFFSET,
            y: 3.82 + props.index * CARD_Y_OFFSET,
          },
        },
      ]),
    ],
    shapes: [
      cardBackground({
        h: 1.28,
        w: 4,
        x: 8.88,
        y: 3.07 + props.index * CARD_Y_OFFSET,
      }),
      ...(driverArrow ? [driverArrow] : []),
      {
        shapeName: 'line',
        options: {
          line: { color: theme.colors.inmarketGreen, width: 1 },
          h: 0,
          w: 1.68,
          x: 11.03,
          y: 3.36 + props.index * CARD_Y_OFFSET,
        },
      },
    ],
  };
};

type GroupedMetric = {
  total: ValueInput;
  exposed: ValueInput;
  control: ValueInput;
};

type KeyDriversProps = {
  penetrationRate: GroupedMetric;
  transactionSize: GroupedMetric;
  transactionRate: GroupedMetric;
};

export const buildKeyDriversCards = (
  props: KeyDriversProps
): Required<Pick<Slide, 'shapes' | 'texts'>> => {
  const cardProps: Omit<CardProps, 'index'>[] = [
    {
      title: {
        text: [
          {
            text: 'MORE ',
          },
          {
            text: 'PEOPLE',
            options: { color: theme.colors.inmarketGreen },
          },
        ],
        options: { fontSize: 12, bold: true },
      },
      kpi: {
        text: formatters.percentage(props.penetrationRate.total, {
          decimals: 2,
        }),
        options: { fontSize: 24 },
      },
      description: {
        text: [
          {
            text: 'The ',
          },
          {
            text: 'lift in % of users ',
            options: { bold: true },
          },
          {
            text: 'who made at least one purchase during the measurement period.',
          },
        ],
        options: { fontSize: 8 },
      },
      metricName: {
        text: 'PENETRATION RATE',
        options: { fontSize: 10, bold: true },
      },
      arrowDirection: (props.penetrationRate.total ?? 0) >= 0 ? 'up' : 'down',
      metricParts: [
        {
          name: {
            text: 'EXPOSED',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.percentage(props.penetrationRate.exposed, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
        {
          name: {
            text: 'CONTROL',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.percentage(props.penetrationRate.control, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
      ],
    },
    {
      title: {
        text: [
          {
            text: 'MORE ',
          },
          {
            text: 'PURCHASES',
            options: { color: theme.colors.inmarketGreen },
          },
        ],
        options: { fontSize: 12, bold: true },
      },
      kpi: {
        text: formatters.percentage(props.transactionSize.total, {
          decimals: 2,
        }),
        options: { fontSize: 24 },
      },
      description: {
        text: [
          {
            text: 'The ',
          },
          {
            text: 'lift in average dollars per purchase ',
            options: { bold: true },
          },
          {
            text: 'during the measurement period.',
          },
        ],
        options: { fontSize: 8 },
      },
      metricName: {
        text: 'TRANSACTION SIZE',
        options: { fontSize: 10, bold: true },
      },
      arrowDirection: (props.transactionSize.total ?? 0) >= 0 ? 'up' : 'down',
      metricParts: [
        {
          name: {
            text: 'EXPOSED',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.currency(props.transactionSize.exposed, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
        {
          name: {
            text: 'CONTROL',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.currency(props.transactionSize.control, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
      ],
    },
    {
      title: {
        text: [
          {
            text: 'MORE ',
          },
          {
            text: 'OFTEN',
            options: { color: theme.colors.inmarketGreen },
          },
        ],
        options: { fontSize: 12, bold: true },
      },
      kpi: {
        text: formatters.percentage(props.transactionRate.total, {
          decimals: 2,
        }),
        options: { fontSize: 24 },
      },
      description: {
        text: [
          {
            text: 'The ',
          },
          {
            text: 'lift in average number of transactions ',
            options: { bold: true },
          },
          {
            text: 'per shopper during the measurement period.',
          },
        ],
        options: { fontSize: 8 },
      },
      metricName: {
        text: 'TRANSACTION RATE',
        options: { fontSize: 10, bold: true },
      },
      arrowDirection: (props.transactionRate.total ?? 0) >= 0 ? 'up' : 'down',
      metricParts: [
        {
          name: {
            text: 'EXPOSED',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.currency(props.transactionRate.exposed, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
        {
          name: {
            text: 'CONTROL',
            options: {
              fontSize: 7,
              bold: true,
              color: theme.colors.inmarketGreen,
            },
          },
          value: {
            text: formatters.number(props.transactionRate.control, {
              decimals: 2,
            }),
            options: { fontSize: 18 },
          },
        },
      ],
    },
  ];

  const cards = cardProps.map((props, index) => buildCard({ ...props, index }));

  return {
    shapes: cards.flatMap(card => card.shapes),
    texts: cards.flatMap(card => card.texts),
  };
};
